import dayjs, { Dayjs } from "dayjs";

export type SearchFunction = (row: any, searchedValue: string, statusFilter?: string, typeFilter?: string) => boolean;

export const getCurrencySymbol = (currency: string): string => {
  switch (currency) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "JPY":
      return "¥";
    case "INR":
      return "₹";
    default:
      return currency;
  }
};

export const defaultSearchFunction: SearchFunction = (row, searchedValue) => {
  if (searchedValue === "") {
    return true;
  }

  const normalizedSearchedValue = searchedValue.replace(/\s+/g, "").toLowerCase();

  return Object.values(row).some((value: any) => {
    if (value) {
      const normalizedValue = value.toString().replace(/\s+/g, "").toLowerCase();
      return normalizedValue.includes(normalizedSearchedValue);
    }
    return false;
  });
};


export const searchWithStatusFilter: SearchFunction = (
  row,
  searchedValue,
  statusFilter,
  typeFilter
) => {
  const searchTerm = searchedValue?.trim().toLowerCase() ?? "";
  const statusTerm = statusFilter?.trim().toLowerCase() ?? "";
  const typeTerm = typeFilter?.trim().toLowerCase() ?? "";

  // Utils
  const isAll = (val: string) =>
    val === "" || val === "all" || val === "allorders" || val === "allgrn";

  const matchesSearch = (): boolean => {
    if (!searchTerm) return true;

    return Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm)
    );
  };

  const matchesStatus = (): boolean => {
    if (isAll(statusTerm)) return true;

    const rowStatus = row.status?.toLowerCase() ?? "";
    const rowGrnStatus = row.grnStatus?.toLowerCase() ?? "";

    return (
      rowStatus === statusTerm ||
      rowGrnStatus === statusTerm ||
      rowStatus.includes(statusTerm) ||
      rowGrnStatus.includes(statusTerm)
    );
  };

  const matchesType = (): boolean => {
    if (isAll(typeTerm)) return true;

    const outletType = row.outletType?.toLowerCase() ?? "";
    return outletType === typeTerm;
  };

  return matchesSearch() && matchesStatus() && matchesType();
};


export const filterData = (
  data: any[],
  searchedValue: string,
  searchFunction: SearchFunction,
  statusFilter?: string,
  typeFilter?: string
): any[] => {
  const statuses: string[] = statusFilter?.split(",") ?? [];
  const types: string[] = typeFilter?.split(",") ?? [];

  if (statuses.length === 0 && types.length === 0) {
    return data.filter((row) => searchFunction(row, searchedValue));
  }

  if (statuses.length > 0 && types.length === 0) {
    return data.filter(row =>
      statuses.some(s =>
        searchFunction(row, searchedValue, s)
      )
    );
  }

  if (types.length > 0 && statuses.length === 0) {
    return data.filter(row =>
      types.some(t =>
        searchFunction(row, searchedValue, undefined, t)
      )
    );
  }

  const result: any[] = [];

  for (const s of statuses) {
    for (const t of types) {
      result.push(
        ...data.filter(row =>
          searchFunction(row, searchedValue, s, t)
        )
      );
    }
  }

  return result;
};

export const updateDatesInLocalStorage = (
  start: Dayjs,
  end: Dayjs,
  navbarParentLabel: string,
  pageID: string
) => {
  const storedData = localStorage.getItem(navbarParentLabel?.toLowerCase());
  const parsedData = storedData ? JSON.parse(storedData) : {};

  parsedData[pageID] = {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };

  localStorage.setItem(navbarParentLabel?.toLowerCase(), JSON.stringify(parsedData));
};

export const getDatesFromLocalStorage = (navbarParentLabel: string, pageID: string, defaultInterval?:number) => {
  const storedData = localStorage.getItem(navbarParentLabel?.toLowerCase());

  if (storedData) {
    const parsedData = JSON.parse(storedData);
    if (parsedData[pageID]) {
      return {
        startDate: dayjs(parsedData[pageID].startDate),
        endDate: dayjs(parsedData[pageID].endDate),
      };
    } else {
      // updateDatesInLocalStorage(dayjs(), dayjs(), navbarParentLabel?.toLowerCase(), pageID);
      return {
        startDate: defaultInterval ? dayjs().subtract(defaultInterval, 'day') : dayjs(),
        endDate: dayjs(),
      };
    }
  }
   else {
    // updateDatesInLocalStorage(dayjs(), dayjs(), navbarParentLabel?.toLowerCase(), pageID);
    return {
      startDate: defaultInterval ? dayjs().subtract(defaultInterval, 'day') : dayjs(),
      endDate: dayjs(),
    };
  }
};

export const removeDatesFromLocalStorage = (activeNavbarParentLabel: string) => {
  const navbarParentLabels = ["sale", "purchase"];
  navbarParentLabels.map((label) => {
    if (label.toLowerCase() != activeNavbarParentLabel.toLowerCase()) {
      localStorage.removeItem(label?.toLowerCase());
    }
  });
};


export const getMultipleFilterBasedData = (
  data: any,
  selectedFilters: Record<string, string[]>,
  filterKeys: string[]
) => {
  return data.filter((order: any) => {
    return filterKeys.every((key) => {
      const orderValue = order[key]?.toLowerCase();
      const selectedValues = selectedFilters[key] || [];
      return selectedValues.length === 0 || selectedValues.some((filter) => filter.toLowerCase() === orderValue);
    });
  });
};

